import { CheckCircleOutlineOutlined, Close } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import toast from "react-hot-toast";
import { AccountModal } from "../accountPieces";
import { AccountInputBox } from "../components/TableActions";
import { accountInputBoxCSS } from "../../subComps/loginPieces";
import { Axios, buttonGradient, dateFormat, svgGradient } from "../../../common/utils";
import { baseApi } from "../../../common/constants";
import { missingTransactionFieldRules } from "../../../common/commonComps";
import { GradientTop, ModalButtonWrap, SecondaryButton } from "../components/pieces";
import { Span } from "../components/Typography";
import {
    styled,
    Box,
    Card,
    Button,
    Autocomplete,
    FormControl,
    FormLabel,
    FormControlLabel,
    Radio,
    RadioGroup,
    useTheme,
} from "@mui/material";
import { useState, useEffect } from "react";
import type React from "react";
import type { Size, AppContext, MissingTransactionStore, MissingTransactionData } from "../../../types";
import type { ValidationError } from "yup";
import dayjs from "dayjs";

interface MissingTransactionsProps {
    missingTransaction: MissingTransactionData | null;
    setMissingTransaction: (data: MissingTransactionData | null) => void;
    size: Size;
    context: AppContext;
}

const Heading = styled(Box)(() => ({
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 16,
}));

const TextLine = styled(Box)(() => ({
    fontSize: 16,
    fontWeight: 400,
    marginBottom: 16,
}));

// No call site passes an `sx` prop to Panel, so the original `...sx` spread was always a no-op; dropped for typing.
const Panel = styled(Box)<{ opacity: number; size: Size }>(({ opacity, size }) => ({
    opacity,
    position: `absolute`,
    display: `flex`,
    flexDirection: `column`,
    minHeight: size === `small` ? `100%` : `calc(100% - 10px)`,
    zIndex: 10 + opacity,
    padding: `40px 30px 30px`,
    transition: `opacity .6s ease`,
}));

const TopDate = styled(Box)(() => ({
    fontWeight: 400,
    fontSize: 14,
}));

const InputBox = styled(AccountInputBox)(accountInputBoxCSS);

const MissingTransactions = ({
    missingTransaction,
    setMissingTransaction,
    size,
    context,
}: MissingTransactionsProps) => {
    const [panel, setPanel] = useState(1);
    const [loading, setLoading] = useState(false);
    const [merchants, setMerchants] = useState<MissingTransactionStore[]>([]);
    const [isValidClick, setIsValidClick] = useState<{ sid?: string; check?: boolean } | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [wherePurchased, setWherePurchased] = useState(`online`);
    const [dateOpen, setDateOpen] = useState(false);
    const [inputVal, setInputVal] = useState<string | undefined>(undefined);
    const theme = useTheme();

    const closeAll = () => {
        setPanel(1);
        setMissingTransaction(null);
        setErrors({});
        setDateOpen(false);
        setInputVal(undefined);
    };

    const submitMissingTrans = () => {
        setLoading(true);
        missingTransactionFieldRules.validator
            .validate(missingTransaction)
            .then(() => {
                Axios.put(`${baseApi}missingtransaction`, { ...missingTransaction, sid: isValidClick!.sid })
                    .then(() => {
                        setLoading(false);
                        setPanel(3);
                    })
                    .catch(() => {
                        setLoading(false);
                        toast.error(`Sorry something went wrong`);
                        closeAll();
                    });
            })
            .catch((e: ValidationError) => {
                setLoading(false);
                // Original assigned the raw message string here; errors is only read via field-name
                // keys, so an equivalent single-key object is used.
                setErrors(e.params?.path ? { [e.params.path as string]: e.message } : { _form: e.message });
            });
    };

    useEffect(() => {
        if (missingTransaction && !merchants.length) {
            setLoading(true);
            Axios.get(`${baseApi}clickedmerchants`)
                .then(r => r.data)
                .then(setMerchants)
                .then(() => setLoading(false))
                .catch(() => toast.error(`Sorry, something went wrong`));
        }
    }, [missingTransaction]);

    useEffect(() => {
        if (missingTransaction?.orderDate && missingTransaction?.storeName) {
            setLoading(true);
            Axios.get(
                `${baseApi}validclick?s=${encodeURIComponent(
                    missingTransaction.storeName.tag
                )}&d=${missingTransaction?.orderDate.getTime()}`
            )
                .then(r => r.data)
                .then(setIsValidClick)
                .then(() => setLoading(false))
                .catch(() => {
                    setLoading(false);
                    toast.error(`Sorry, something went wrong`);
                });
        }
    }, [missingTransaction?.orderDate, missingTransaction?.storeName]);

    return (
        <AccountModal
            sx={{ height: size === `small` ? `100%` : 710, borderRadius: size === `small` ? 0 : 10, overflow: `auto` }}
            onClose={closeAll}
            size={size}
            // enableScrollLock is forwarded to the underlying Modal; AccountModal's styled generic
            // (in accountPieces) does not re-declare it, so it is passed via spread.
            {...{ enableScrollLock: true }}
            open={!!missingTransaction}
        >
            <Card sx={{ boxSizing: `border-box` }} className="modalContent">
                {size === `small` && (
                    <Box p={2} sx={{ textAlign: `center` }}>
                        <Box component="img" sx={{ width: 150 }} src={context?.adminConfig?.mall?.Color_Desktop_Logo} />
                    </Box>
                )}
                <Close
                    sx={{
                        position: `absolute`,
                        top: 24,
                        right: 10,
                        zIndex: 30,
                        cursor: `pointer`,
                        opacity: 0.55,
                        height: 25,
                        width: 25,
                        zindex: 30,
                    }}
                    onClick={closeAll}
                />
                {size !== `small` && <GradientTop />}

                {panel === 1 && (
                    <Panel size={size} opacity={Number(panel === 1)}>
                        <Heading>Missing Cash Back</Heading>
                        <TextLine sx={{ marginBottom: `35px` }}>
                            Please check your account activity before reporting missing cash back. It can take up to 5
                            days for your purchases to appear in your account.
                        </TextLine>
                        <FormControl
                            sx={{
                                width: `100%`,
                            }}
                        >
                            <Box
                                sx={{
                                    display: `flex`,
                                    justifyContent: `space-between`,
                                }}
                            >
                                <FormLabel sx={{ fontSize: 16, fontWeight: 600, color: `#333` }} htmlFor="storeName">
                                    Store Name
                                </FormLabel>
                            </Box>
                            <Autocomplete<MissingTransactionStore>
                                fullWidth
                                value={missingTransaction?.storeName as MissingTransactionStore | null}
                                onInputChange={v => setInputVal((v.target as HTMLInputElement).value)}
                                open={!!inputVal}
                                onBlur={() => setInputVal(undefined)}
                                filterOptions={(o, v) =>
                                    o.filter(val =>
                                        val.label
                                            .toLowerCase()
                                            .split(` `)
                                            .some(x => x.startsWith(v.inputValue.toLowerCase()))
                                    )
                                }
                                inputValue={inputVal as string}
                                popupIcon={null}
                                onChange={(event, newInputValue, reason) => {
                                    if (reason === `clear`) {
                                        setMissingTransaction({ ...missingTransaction, storeName: null });
                                    } else {
                                        setMissingTransaction({ ...missingTransaction, storeName: newInputValue });
                                    }
                                }}
                                options={merchants}
                                renderInput={params => (
                                    <InputBox
                                        sx={{
                                            maxWidth: `unset`,
                                            "& .MuiInputBase-root": { padding: `0 !important` },
                                            "& input": {
                                                fontWeight: 400,
                                                height: `23px`,
                                                padding: `10px 14px !important`,
                                            },
                                            marginTop: `5px`,
                                        }}
                                        {...params}
                                    />
                                )}
                            />
                        </FormControl>
                        <FormControl
                            sx={{
                                width: `100%`,
                                marginBottom: `24px`,
                            }}
                        >
                            <Box
                                sx={{
                                    display: `flex`,
                                    justifyContent: `space-between`,
                                }}
                            >
                                <FormLabel sx={{ fontSize: 16, fontWeight: 600, color: `#333` }} htmlFor="purchaseDate">
                                    Purchase Date
                                </FormLabel>
                            </Box>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    name="purchaseDate"
                                    // id is forwarded to the picker root at runtime but is not in DatePickerProps, so it is passed via spread.
                                    {...{ id: `purchaseDate` }}
                                    disableFuture
                                    open={dateOpen}
                                    onOpen={() => setDateOpen(true)}
                                    onClose={() => setDateOpen(false)}
                                    slotProps={{
                                        // placeholder is a valid TextField prop passed through at runtime, but the
                                        // picker's slotProps type does not surface it, so the object is cast.
                                        textField: {
                                            onClick: () => setDateOpen(true),
                                            onKeyDown: (e: React.KeyboardEvent) => e.preventDefault(),
                                        } as {
                                            placeholder: string;
                                            onClick: () => void;
                                            onKeyDown: (e: React.KeyboardEvent) => void;
                                        },
                                    }}
                                    sx={{
                                        background: `#F5F4F2`,
                                        borderRadius: `4px`,
                                        boxShadow: `0 0 0 50px #F5F4F2 inset !important`,
                                        marginTop: `5px`,
                                        "& fieldset": {
                                            border: `none`,
                                        },
                                        input: {
                                            padding: `10px 14px !important`,
                                            height: `25px`,
                                            cursor: `pointer`,
                                            color: missingTransaction?.orderDate ? `` : `#F5F4F2`,
                                            userSelect: `none`,
                                            webkitUserSelect: `none`,
                                            "&::selection": {
                                                background: `#F5F4F2`,
                                            },
                                        },
                                    }}
                                    value={missingTransaction?.orderDate ? dayjs(missingTransaction.orderDate) : null}
                                    onChange={v =>
                                        setMissingTransaction({ ...missingTransaction, orderDate: v?.toDate() })
                                    }
                                />
                            </LocalizationProvider>
                        </FormControl>
                        <FormControl
                            sx={{
                                width: `100%`,
                                marginBottom: `16px`,
                            }}
                        >
                            <Box
                                sx={{
                                    display: `flex`,
                                    justifyContent: `space-between`,
                                }}
                            >
                                <FormLabel
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 600,
                                        color: `#333`,
                                        "&.MuiFormLabel-root.Mui-focused": {
                                            color: `#333`,
                                        },
                                    }}
                                    htmlFor="row-radio-buttons-group"
                                >
                                    Where did you purchase?
                                </FormLabel>
                            </Box>
                            <RadioGroup
                                row
                                value={wherePurchased}
                                onChange={v => setWherePurchased(v.target.value)}
                                aria-labelledby="demo-row-radio-buttons-group-label"
                                name="row-radio-buttons-group"
                            >
                                <FormControlLabel defaultChecked value="online" control={<Radio />} label="Online" />
                                <FormControlLabel value="instore" control={<Radio />} label="In Store" />
                            </RadioGroup>
                        </FormControl>
                        {wherePurchased === `instore` && missingTransaction?.storeName && (
                            <TextLine
                                sx={{
                                    padding: `10px`,
                                    color: theme.palette.account.accentGradientEnd,
                                    background: `${theme.palette.account.accent}10`,
                                }}
                            >
                                {missingTransaction?.storeName?.label} only provides cash back when your purchase is
                                made online.
                            </TextLine>
                        )}
                        {isValidClick?.check === false &&
                            missingTransaction?.storeName &&
                            missingTransaction?.orderDate &&
                            wherePurchased === `online` && (
                                <TextLine
                                    sx={{
                                        padding: `10px`,
                                        color: theme.palette.account.accentGradientEnd,
                                        background: `${theme.palette.account.accent}10`,
                                    }}
                                >
                                    Cash Back was not activated for{` `}
                                    {missingTransaction?.storeName?.label || `that merchant`} on{` `}
                                    {dateFormat(missingTransaction?.orderDate)}.
                                </TextLine>
                            )}
                        <ModalButtonWrap>
                            <Button
                                disabled={loading}
                                size="small"
                                variant="contained"
                                onClick={closeAll}
                                sx={{
                                    border: `1px solid ${theme.palette.account.accent}`,
                                    color: theme.palette.account.accent,
                                    background: `#FFF`,
                                    padding: `4px 8px`,
                                    minWidth: `unset`,
                                    borderRadius: 2,
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={
                                    !(
                                        wherePurchased === `online` &&
                                        missingTransaction?.storeName &&
                                        missingTransaction?.orderDate
                                    ) ||
                                    loading ||
                                    isValidClick?.check !== true
                                }
                                onClick={() => setPanel(2)}
                                sx={{
                                    background: buttonGradient(theme),
                                    padding: `4px 8px`,
                                    minWidth: `unset`,
                                    borderRadius: 2,
                                    "&.Mui-disabled": {
                                        background: `#999 !important`,
                                        opacity: 0.5,
                                    },
                                }}
                            >
                                Next
                            </Button>
                        </ModalButtonWrap>
                    </Panel>
                )}
                {panel === 2 && (
                    <Panel size={size} opacity={Number(panel === 2)}>
                        <TopDate>{dateFormat(missingTransaction?.orderDate)}</TopDate>
                        <Heading>{missingTransaction?.storeName?.label}</Heading>
                        <TextLine>
                            Please provide information about your purchase. Reference the merchant&apos;s confirmation
                            email. We won&apos;t be able to research the transaction if the information is incorrect.
                        </TextLine>
                        {missingTransactionFieldRules.fields.map(f => (
                            <FormControl
                                key={f.l}
                                sx={{
                                    width: `100%`,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: `flex`,
                                        justifyContent: `space-between`,
                                    }}
                                >
                                    <FormLabel sx={{ fontSize: 16, fontWeight: 600, color: `#333` }} htmlFor={f.v}>
                                        {f.l}
                                    </FormLabel>
                                </Box>
                                {f.pre && (
                                    <Span
                                        sx={{
                                            position: `absolute`,
                                            left: `14px`,
                                            top: `38px`,
                                            zIndex: `15`,
                                            fontSize: `16px`,
                                        }}
                                    >
                                        {f.pre}
                                    </Span>
                                )}
                                <InputBox
                                    name={f.v}
                                    type="text"
                                    id={f.v}
                                    fullWidth
                                    helperText={errors[f.v]}
                                    error={!!errors[f.v]}
                                    value={missingTransaction?.[f.v]}
                                    sx={{
                                        height: `48px`,
                                        maxWidth: `unset`,
                                        "& .MuiInputBase-root": { padding: `0 !important` },
                                        input: {
                                            fontWeight: 500,
                                            height: `23px`,
                                            ...(f.pre ? { paddingLeft: `24px` } : {}),
                                        },
                                        marginTop: `5px`,
                                    }}
                                    onChange={v => {
                                        setErrors({});
                                        setMissingTransaction({
                                            ...missingTransaction,
                                            [f.v]: f.regex ? v.target.value.replace(f.regex, ``) : v.target.value,
                                        });
                                    }}
                                />
                            </FormControl>
                        ))}
                        <ModalButtonWrap>
                            <SecondaryButton disabled={loading} onClick={() => setPanel(1)}>
                                Back
                            </SecondaryButton>
                            <Button
                                size="small"
                                disabled={
                                    !(
                                        Object.keys(missingTransaction || {}).filter(a => missingTransaction![a])
                                            .length === 6
                                    ) ||
                                    loading ||
                                    isValidClick?.check !== true
                                }
                                variant="contained"
                                onClick={submitMissingTrans}
                                sx={{
                                    background: buttonGradient(theme),
                                    padding: `4px 8px`,
                                    minWidth: `unset`,
                                    borderRadius: 2,
                                    "&.Mui-disabled": {
                                        background: `#999 !important`,
                                        opacity: 0.5,
                                    },
                                }}
                            >
                                Submit
                            </Button>
                        </ModalButtonWrap>
                    </Panel>
                )}
                {panel === 3 && (
                    <Panel size={size} opacity={Number(panel === 3)}>
                        <Box my="24px" sx={{ textAlign: `center` }}>
                            {svgGradient(theme)}
                            <CheckCircleOutlineOutlined
                                fill="multiStopGradient"
                                sx={{ height: 80, width: 80, fill: `url(#multiStopGradient)` }}
                            />
                        </Box>
                        <Heading sx={{ textAlign: `center` }}>Submitted</Heading>
                        <TextLine my="24px" sx={{ textAlign: `center`, marginBottom: `60px` }}>
                            Your missing cash back has been reported. It could take up to 8 weeks for the merchant to
                            research your purchase. If your cash back is approved it will appear in your account.
                        </TextLine>
                        <ModalButtonWrap sx={{ textAlign: `center` }}>
                            <Button
                                disabled={loading}
                                size="small"
                                variant="contained"
                                onClick={closeAll}
                                sx={{
                                    background: buttonGradient(theme),
                                    padding: `4px 8px`,
                                    minWidth: `unset`,
                                    borderRadius: 2,
                                }}
                            >
                                Close
                            </Button>
                        </ModalButtonWrap>
                    </Panel>
                )}
            </Card>
        </AccountModal>
    );
};

export default MissingTransactions;
